import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import { useI18nStore } from "@/stores/i18nStore";
import { Edit3, Heart, Save } from "lucide-react";
import React, { useState } from "react";
import { EmotionalWellbeingChart } from "../charts/EmotionalWellbeingChart";

interface EmotionalWellbeingBoxProps {
  emotionalWellbeing: number;
  onEmotionalChange: (value: number) => void;
  onSave: () => Promise<void>;
  lastRecordedAt?: string;
  wellbeingHistory?: Array<{
    date: string;
    emotionalWellbeing?: number;
    emotionalWellness?: number;
    timestamp: string;
    formattedDate: string;
  }>;
}

export const EmotionalWellbeingBox: React.FC<EmotionalWellbeingBoxProps> = ({
  emotionalWellbeing,
  onEmotionalChange,
  onSave,
  lastRecordedAt,
  wellbeingHistory,
}) => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const getWellbeingEmoji = (score: number) => {
    switch (score) {
      case 1:
        return "😢"; // Very sad
      case 2:
        return "😟"; // Worried/concerned
      case 3:
        return "😐"; // Neutral
      case 4:
        return "😊"; // Happy
      case 5:
        return "😁"; // Very happy
      default:
        return "😐";
    }
  };

  const getWellbeingLabel = (score: number) => {
    const labels = {
      1: t.emotionalVeryPoor,
      2: t.emotionalPoor,
      3: t.emotionalFair,
      4: t.emotionalGood,
      5: t.emotionalExcellent,
    };

    return labels[score as keyof typeof labels] || t.unknown;
  };

  const getLastRecordedTimestamp = () =>
    fmt.dateTime(lastRecordedAt || new Date());

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave();
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
          <Heart className="w-5 h-5 text-purple-600" />
          <span>{t.emotionalWellbeing}</span>
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
            title={isEditing ? t.cancel : t.edit}
          >
            <Edit3 className="w-4 h-4" />
          </button>
          {isEditing && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-3 py-1 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-colors flex items-center space-x-1"
            >
              {isSaving ? (
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
              ) : (
                <Save className="w-3 h-3" />
              )}
              <span>{isSaving ? t.saving : t.save}</span>
            </button>
          )}
        </div>
      </div>

      {/* Assessment Section */}
      <div className="space-y-4">
        <div>
          <div className="block text-sm font-medium text-gray-700 mb-3">
            {t.howDoYouFeelEmotionally}
          </div>
          <div className="flex items-center space-x-3">
            {[1, 2, 3, 4, 5].map((score) => (
              <button
                key={`emotional-${score}`}
                onClick={() => isEditing && onEmotionalChange(score)}
                disabled={!isEditing}
                className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                  emotionalWellbeing === score
                    ? "border-purple-500 bg-purple-50 scale-110"
                    : "border-gray-200 hover:border-gray-300 hover:scale-105"
                } ${!isEditing ? "cursor-default" : "cursor-pointer"}`}
                title={getWellbeingLabel(score)}
              >
                <span className="text-2xl">{getWellbeingEmoji(score)}</span>
              </button>
            ))}
          </div>
          <div className="mt-2">
            <span className="text-sm text-gray-600">
              {t.currentStatus}: {getWellbeingLabel(emotionalWellbeing)}
            </span>
          </div>
        </div>

        {/* Timestamp */}
        <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
          {t.lastUpdate}: {getLastRecordedTimestamp()}
        </div>
      </div>

      {/* 365-Day Trend Chart */}
      <div className="mt-8 pt-6 border-t border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {t.trendHistory}
        </h3>
        <EmotionalWellbeingChart
          key={`emotional-chart-${wellbeingHistory?.length || 0}-${wellbeingHistory?.[0]?.timestamp || "empty"}`}
          data={wellbeingHistory || []}
          className="mt-4"
        />
        {(!wellbeingHistory || wellbeingHistory.length === 0) && (
          <div className="text-center py-8 text-gray-500">
            <p className="mb-2">📊 {t.noTrendData}</p>
            <p className="text-sm">{t.noTrendDataDesc}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmotionalWellbeingBox;
